// import { LinearGradient } from 'expo-linear-gradient';
import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { twJoin } from 'tailwind-merge';

import { AuthCard } from '@/components/authCard';
import { ContentLibraryItemDownloadLink } from '@/components/contentLibrary/contentLibraryItemDownloadLink';
import { ContentLibraryItemFeedback } from '@/components/contentLibrary/contentLibraryItemFeedback';
import { ContentLibraryItemHeader } from '@/components/contentLibrary/contentLibraryItemHeader';
import { getScreenColumnClasses } from '@/components/screenColumn';
import { ScrollView } from '@/components/ui/scroll-view';
import { View } from '@/components/ui/view';
import { useAbility } from '@/hooks/useAbility';
import { usePrimumAssets } from '@/hooks/usePrimumAssets';
import { type ContentLibraryItem, type LikeStatus } from '@/types/contentLibraryItems';
import { getArticleContentMarkup } from '@/utils/contentLibraryHelpers';

const SCREEN_COLUMN = getScreenColumnClasses({ maxWidth: 'max-w-[850px]', gutter: 'p-5 md:px-10' });

const ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_MINUS_GRADIENT = 200;

const FULL_ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_WITH_GRADIENT =
  ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_MINUS_GRADIENT + 128;

export function ContentLibraryItemArticle({
  changeLikeStatus,
  contentLibraryItem,
  isPublic,
  likeStatus,
}: {
  changeLikeStatus: (newLikeStatus: LikeStatus) => void;
  contentLibraryItem: ContentLibraryItem;
  isPublic: boolean;
  likeStatus: LikeStatus;
}) {
  const { getCloudFrontUrl } = usePrimumAssets();

  const canReadContentLibrary = useAbility().can('read', 'ContentLibraryItem');

  const contentMarkupLines = getArticleContentMarkup(
    String(contentLibraryItem.articleContent),
    getCloudFrontUrl,
    contentLibraryItem.id,
    isPublic
  );

  return (
    <>
      <ScrollView
        contentInsetAdjustmentBehavior="automatic"
        className={twJoin('no-scrollbar', SCREEN_COLUMN.frame)}
        contentContainerClassName={SCREEN_COLUMN.content}
        showsVerticalScrollIndicator={false}>
        <ContentLibraryItemHeader contentLibraryItem={contentLibraryItem} />
        <ContentLibraryItemDownloadLink
          contentLibraryItem={contentLibraryItem}
          isPublic={isPublic}
        />
        <View
          className={twJoin(
            'relative',
            !canReadContentLibrary && !isPublic ? 'overflow-hidden' : ''
          )}
          style={
            !canReadContentLibrary && !isPublic
              ? { height: FULL_ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_WITH_GRADIENT }
              : undefined
          }>
          {contentMarkupLines.map((contentMarkupLine) => contentMarkupLine)}
          {!canReadContentLibrary && !isPublic && isWeb && (
            <View
              className="pointer-events-none absolute left-0 right-0 h-32 bg-gradient-to-b from-transparent to-white"
              style={{
                top: ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_MINUS_GRADIENT,
              }}
            />
            // To-do: Bring back for mobile later. Requires us to create a new app build with the expo-linear-gradient package.
            // <LinearGradient
            //   colors={['rgba(255, 255, 255, 0)', 'rgba(255, 255, 255, 1)']}
            //   pointerEvents="none"
            //   style={{
            //     height: 128,
            //     left: 0,
            //     position: 'absolute',
            //     right: 0,
            //     top: ARTICLE_HEIGHT_TO_SHOW_FOR_UNAUTHENTICATED_USER_MINUS_GRADIENT,
            //   }}
            // />
          )}
        </View>
        {!canReadContentLibrary && !isPublic && (
          // To-do: This was added to make mobile look decent without the gradient.
          <View className={!isWeb ? 'mt-6' : ''}>
            <AuthCard text="Sign in or create an account to read the full article." />
          </View>
        )}
        <ContentLibraryItemFeedback changeLikeStatus={changeLikeStatus} likeStatus={likeStatus} />
      </ScrollView>
    </>
  );
}
